import { clsx } from 'clsx';
import moment from 'moment-timezone';
import { useNotify, useRefresh } from 'react-admin';
import { Link } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import { Button, MenuItem, Select } from '@mui/material';
import Box from '@mui/material/Box';
import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import {
  AutoComplete,
  Checkbox,
  Col,
  DatePicker,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Row,
  UploadFile,
} from 'antd';

import { creditHttp, financeWithdrawalHttp, s3http } from '@network';
import { formatMoney, moneyFormatter, onBeforeNumberInput, showAxiosError } from '@utils';
import {
  CreateFinanceWithdrawalParams,
  CreditInvoice, CreditInvoiceFile,
  FinanceDocument,
  FinanceWithdrawal,
  FinanceWithdrawalAccountOption,
  FinanceWithdrawalOptions,
  FinanceWithdrawalOrderOption,
  Order,
} from '@types';
import { FinanceDocuments } from '@pages/finance-withdrawal/FinanceDocuments';
import { InfoTooltip } from '@components';
import cls from './finance-withdrawal-form.module.css';

interface Props {
  financeWithdrawal?: FinanceWithdrawal | null;
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  clientName: string;
  setClientName: Dispatch<SetStateAction<string>>;
  submitted: boolean;
  setSubmitted: Dispatch<SetStateAction<boolean>>;
  onDelete?: () => void;
  options: FinanceWithdrawalOptions;
  initial: CreateFinanceWithdrawalParams;
  onSubmit: (params: CreateFinanceWithdrawalParams) => Promise<void>;
  form: FormInstance<CreateFinanceWithdrawalParams>;
  invoiceDocument?: CreditInvoiceFile | null;
  initialAccount?: FinanceWithdrawalAccountOption | null;
  invoiceOrderId?: number | null;
  prefilledOrder?: Order | null;
}

interface OptionProps {
  key: number;
  value: string;
  label: JSX.Element;
}

interface RenderOptionProps {
  idx: number;
  code: string;
  disabled?: boolean;
  balanceTitle?: string;
  balance?: number;
  showAmount?: boolean;
  hasFinanceAccount?: boolean;
}

export const FinanceWithdrawalForm = ({
  financeWithdrawal,
  loading,
  setLoading,
  onDelete,
  onSubmit,
  options,
  initial,
  form,
  clientName,
  setClientName,
  setSubmitted,
  invoiceDocument,
  invoiceOrderId,
  prefilledOrder,
  initialAccount,
}: Props) => {
  const hasId = !!financeWithdrawal?.id;
  const isAirtable = !!financeWithdrawal?.airtableId;
  const refresh = useRefresh();
  const [receipts, setReceipts] = useState<FinanceDocument[]>(financeWithdrawal?.receipts || []);
  const [invoices, setInvoices]
    = useState<(CreditInvoiceFile | FinanceDocument)[]>(financeWithdrawal?.invoices || []);
  const usedFiles = useRef<Record<string, boolean>>({});
  const notify = useNotify();
  const labelCol = { xs: 24, sm: 24, md: 8, lg: 6, xl: 8, xxl: 6 };
  const [account, setAccount]
    = useState<FinanceWithdrawalAccountOption | null>(initialAccount || null);
  const [accountsList, setAccountsList] = useState<OptionProps[]>([]);
  const [creditsList, setCreditsList] = useState<OptionProps[]>([]);
  const [date, setDate] = useState<string>(initial.paymentDate);
  const [ordersList, setOrdersList] = useState<FinanceWithdrawalOrderOption[]>([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const [ordersListMap, setOrdersListMap]
    = useState<Record<number, FinanceWithdrawalOrderOption>>({});
  const [currentInvoice, setCurrentInvoice] = useState<CreditInvoice | null>(null);

  const readonly = false;
  const currentConcept = Form.useWatch('concept', form);
  const currentUuid = Form.useWatch('uuid', form);
  const creditId = Form.useWatch('creditId', form);
  const isFee = Form.useWatch('fee', form);
  const feePercent = Form.useWatch('feePercent', form) || 0;
  const amount = Form.useWatch('amount', form) || 0;
  const isUsd = Form.useWatch('isUsd', form) ?? false;
  const exchangeRate = Form.useWatch('exchangeRate', form) || 0;
  const showFee = ['RI', 'PP', 'RD'].includes(currentConcept);

  useEffect(() => {
    if (amount && exchangeRate) {
      const amountUsd = +(amount / exchangeRate).toFixed(2);
      form.setFieldValue('amountUsd', amountUsd);
    } else {
      form.setFieldValue('amountUsd', 0);
    }
  }, [amount, exchangeRate]);

  useEffect(() => {
    if (['RI', 'PP'].includes(currentConcept)) {
      form.setFieldValue('purpose', 'Operations');
    }
  }, [currentConcept]);

  useEffect(() => {
    if (financeWithdrawal) {
      setReceipts(financeWithdrawal.receipts);
      setInvoices(financeWithdrawal.invoices);
    }

    if (financeWithdrawal?.creditCode) {
      const selected = options.credits.find(a => a.code === financeWithdrawal?.creditCode?.trim());

      setOrdersList(selected?.orders || []);
      setSelectedOrderIds(financeWithdrawal?.orderIds || []);

      setOrdersListMap((selected?.orders || []).reduce((result, item) => ({
        ...result,
        [item.id]: item,
      }), {}));
    }

    if (financeWithdrawal?.uuid) {
      form.setFieldValue('uuid', financeWithdrawal.uuid);
    }
  }, [financeWithdrawal]);

  useEffect(() => {
    if (invoiceOrderId) {
      const selected = options.credits.find(a => a.id === creditId);
      setOrdersList(selected?.orders || []);

      const currentOrderIds = form.getFieldValue('orderIds') || [];
      setSelectedOrderIds(prevState => [...new Set([...prevState, invoiceOrderId])]);

      setOrdersListMap((selected?.orders || []).reduce((result, item) => ({
        ...result,
        [item.id]: item,
      }), {}));

      if (!currentOrderIds.includes(invoiceOrderId)) {
        form.setFieldValue('orderIds', [...currentOrderIds, invoiceOrderId]);
      }
    }
  }, [invoiceOrderId, creditId]);

  useEffect(() => {
    if (currentUuid) {
      (async () => {
        try {
          const currentOrderIds = form.getFieldValue('orderIds') || [];

          const nextCreditInvoice = await creditHttp.getInvoiceByUuid(currentUuid);

          if (nextCreditInvoice) {
            setCurrentInvoice(nextCreditInvoice);
          } else {
            setSelectedOrderIds(currentOrderIds.filter(id => id !== currentInvoice?.order?.id));
            form.setFieldValue('orderIds', currentOrderIds.filter(id => id !== currentInvoice?.order?.id));

            return;
          }

          const orderId = nextCreditInvoice?.order?.id;

          if (orderId && ordersListMap[orderId]) {
            setSelectedOrderIds(prevState => [...new Set([...prevState, orderId])]);

            if (!currentOrderIds.includes(orderId)) {
              form.setFieldValue('orderIds', [...currentOrderIds, orderId]);
            }
          }
        } catch (e) {
          console.error(e);
        }
      })();
    }
  }, [currentUuid]);

  const onUuidChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    form.setFieldValue('uuid', e.target.value);
  };

  const renderOption = ({
    idx,
    code,
    disabled,
    balanceTitle,
    balance,
    showAmount,
    hasFinanceAccount,
  }: RenderOptionProps): OptionProps => ({
    key: idx,
    value: disabled
      ? ''
      : showAmount
        ? `${code} / ${balanceTitle ?? moneyFormatter.format(balance || 0)} ${!hasFinanceAccount ? '/ No financial account linked to this user' : ''}`
        : code,
    label: (
      <span className={clsx(cls.option, disabled && cls.disabledOption)}>
        {showAmount
          ? `${code} / ${balanceTitle ?? moneyFormatter.format(balance || 0)} ${!hasFinanceAccount ? '/ No financial account linked to this user' : ''}`
          : code
        }
      </span>
    ),
  });

  const onAccountSearch = (searchText: string) => {
    setAccountsList(
      !searchText
        ? options.accounts.map((a, idx) =>
          renderOption({
            idx,
            code: a.code,
            balance: a.balance,
          }),
        )
        : options.accounts
          .filter(a => a.code.toLowerCase().includes(searchText.toLowerCase()))
          .map((a, idx) =>
            renderOption({
              idx,
              code: a.code,
              balance: a.balance,
            })),
    );
  };

  const onAccountSelect = (data: string) => {
    const selected = options.accounts.find(a => a.code === data);
    setAccount(selected ? selected : null);
    form.setFieldValue('accountId', selected?.id || null);
  };

  const onAccountChange = (data: string) => {
    !data && onAccountClear();
  };

  const onAccountClear = () => {
    form.setFieldValue('accountId', null);
    setAccount(null);
    setAccountsList(options.accounts.map((a, idx) =>
      renderOption({
        idx,
        code: a.code,
        balance: a.balance,
      })),
    );
  };

  const onCreditSearch = (searchText: string) => {
    const nextOptions = options.credits
      .filter(a => searchText ? a.code.toLowerCase().includes(searchText.toLowerCase()) : true)
      .map((a, idx) => renderOption({
        idx,
        code: a.code,
        balanceTitle: a.balanceTitle,
        balance: a.balance,
        showAmount: true,
        hasFinanceAccount: a.hasFinanceAccount,
      }));
    setCreditsList(nextOptions);
  };

  const onCreditSelect = (data: string) => {
    const [code] = data.split('/');
    const selected = options.credits.find(a => a.code === code.trim());
    setClientName(selected?.accredited || '');
    form.setFieldValue('creditId', selected?.id || null);
    form.setFieldValue('isUsd', selected?.balanceTitle?.includes('USD') ? true : false);

    setOrdersList(selected?.orders || []);
    setOrdersListMap((selected?.orders || []).reduce((result, item) => ({
      ...result,
      [item.id]: item,
    }), {}));
    setSelectedOrderIds([]);
  };

  const onCreditClear = () => {
    form.setFieldValue('creditId', null);
    form.setFieldValue('isUsd', false);
    setCreditsList(options.credits.map((a, idx) =>
      renderOption({
        idx,
        code: a.code,
        balanceTitle: a.balanceTitle,
        balance: a.balance,
        showAmount: true,
        hasFinanceAccount: a.hasFinanceAccount,
      })),
    );
    setClientName('');
    form.setFieldValue('orderIds', []);
  };

  const onCreditChange = (data: string) => {
    setSelectedOrderIds([]);
    form.setFieldValue('orderIds', []);
    !data && onCreditClear();
  };

  const onDateChange = async (dt: moment.Moment | null) => {
    setDate(moment(dt).format('DD-MM-YYYY'));
  };

  const onOrdersChange = (e: any) => {
    setSelectedOrderIds(e.target.value);
  };

  const submit = async () => {
    if (loading) return;
    if (readonly) {
      return notify('Read only permission');
    }
    if (isFee && !(feePercent > 0)) {
      return notify('Add Fee percent is missing');
    }

    setSubmitted(true);
    try {
      setLoading(true);
      await form.validateFields();
      const params = form.getFieldsValue();
      params.paymentDate = moment(date, 'DD-MM-YYYY').format('YYYY-MM-DD');
      if (!isFee || !showFee) {
        params.fee = false;
        params.feePercent = null;
      }
      await onSubmit({
        ...params, orderIds: prefilledOrder ? [prefilledOrder.id] : selectedOrderIds,
      });
    } catch (e) {
      // @ts-ignore
      if (e.errorFields?.length) {
        notify('Please fill in all required fields of the form', { type: 'error' });
      }
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setAccountsList(options.accounts.map((a, idx) =>
      renderOption({
        idx,
        code: a.code,
        balance: a.balance,
      })),
    );
    setCreditsList(options.credits.map((a, idx) =>
      renderOption({
        idx,
        code: a.code,
        balanceTitle: a.balanceTitle,
        balance: a.balance,
        showAmount: true,
        hasFinanceAccount: a.hasFinanceAccount,
      })),
    );
  }, [options]);

  useEffect(() => {
    if (initial.accountId) {
      const item = options.accounts.find(a => a.id === initial.accountId);
      item && setAccount(item);
    }
  }, []);

  useEffect(() => {
    if (invoiceDocument) {
      setInvoices(prev => {
        if (prev.find(i => i.id === invoiceDocument.id)) {
          return prev;
        }

        return [...prev, invoiceDocument];
      });
    }
  }, [invoiceDocument]);

  const onUpload = (type: 'invoice' | 'receipt') => async (uploadFiles: UploadFile[]) => {
    if (!financeWithdrawal) {
      return notify('Please save Withdrawal first!');
    }
    if (readonly) {
      return notify('Only Superadmins are allowed to Upload Invoice/Receipt document');
    }
    // check unique
    const files: UploadFile[] = [];
    uploadFiles.forEach(file => {
      const uid = file.uid;
      if (!usedFiles.current[uid]) {
        usedFiles.current[uid] = true;
        files.push(file);
      }
    });
    if (files.length === 0) {
      return;
    }

    // upload files
    try {
      const s3files = await Promise.all(
        files.map(file => s3http.uploadFinanceFile(file.originFileObj as File)),
      );
      const updatedDocuments = await financeWithdrawalHttp.addDocument({
        id: financeWithdrawal.id,
        fileIds: s3files.map(file => file.id),
        type,
      });
      type === 'invoice' ? setInvoices(updatedDocuments) : setReceipts(updatedDocuments);
      refresh();
    } catch (err: any) {
      console.error(err);
      showAxiosError(err);
    } finally {
      setLoading(false);
    }
  };

  const onRemove = (type: 'invoice' | 'receipt') => async (documentId: number) => {
    if (!financeWithdrawal) {
      return notify('Please save Withdrawal first!');
    }
    try {
      const updatedDocuments = await financeWithdrawalHttp.removeDocument({
        id: financeWithdrawal.id,
        documentId,
      });
      type === 'invoice' ? setInvoices(updatedDocuments) : setReceipts(updatedDocuments);
      refresh();
    } catch (err: any) {
      console.error(err);
      showAxiosError(err);
    }
  };

  const initAccount = options.accounts.find(a => a.id === form.getFieldValue('accountId'));
  const initCredit = options.credits.find(c => c.id === form.getFieldValue('creditId'));
  const showOrders = currentConcept === 'RI';
  const hasOrders = ordersList.length > 0 || !!currentInvoice?.order;

  const creditsAutocompleteOptions = creditsList.map(option => ({
    value: option.value,
    label: option.label?.props?.children,
  }));

  return (
    <div className={cls.formWrap}>
      <Row gutter={{ xl: 80, xxl: 120 }} className={cls.infoWrapper}>
        <Col xs={24} xl={12} className={cls.infoCol}>
          <div className={cls.fieldRow}>
            <div className={cls.fieldCol}>
              <Form.Item
                label="Concept*"
                name="concept"
                className={clsx(cls.formItem, cls.formItemHorizontal, readonly && cls.disabled)}
                labelCol={{ xs: 24, sm: 8, lg: 6, xl: 8, xxl: 6 }}
                wrapperCol={{ xs: 24, sm: 16, lg: 12, xl: 7, xxl: 6 }}
                rules={[{ required: true, message: '' }]}
              >
                <Select
                  name="concept"
                  variant="outlined"
                  className={cls.selectField}
                  disabled={readonly}>
                  {options.concept.map(value =>
                    <MenuItem key={value} value={value}>{value}</MenuItem>,
                  )}
                </Select>
              </Form.Item>
            </div>
            <div className={cls.fieldCol}>
              <div className={cls.conceptValue}>
                <span className={cls.conceptValueMonth}>
                  {financeWithdrawal?.conceptMonth || options.conceptMonth}
                </span>
                <span className={cls.conceptValueSplitter}>-</span>
                <span className={cls.conceptValueNumber}>
                  {financeWithdrawal?.conceptNumber || options.conceptNumber}
                </span>
              </div>
            </div>
          </div>

          <Form.Item
            label="Account"
            name="accountId"
            className={clsx(cls.formItem, readonly && cls.disabled)}
            labelCol={labelCol}
          >
            <>
              <InfoTooltip title="Finance Accounts, full list" formItem />
              <AutoComplete
                defaultValue={initAccount?.code}
                disabled={readonly}
                options={accountsList}
                className={clsx(
                  cls.selectField,
                  cls.autocompleteField,
                )}
                popupClassName={cls.popup}
                notFoundContent="Code is not found"
                allowClear
                onSelect={onAccountSelect}
                onSearch={onAccountSearch}
                onChange={onAccountChange}
                onClear={onAccountClear}
                placeholder="Search Account code"
              />
            </>
          </Form.Item>

          <Form.Item
            label="Credit code"
            name="creditId"
            className={clsx(cls.formItem, readonly && cls.disabled)}
            labelCol={labelCol}
          >
            <>
              <InfoTooltip title="Credits with User which has Finance Account attached" formItem />
              <AutoComplete
                defaultValue={initCredit
                  ? `${initCredit.code} / ${initCredit.balanceTitle ?? moneyFormatter.format(initCredit.balance || 0)} ${!initCredit.hasFinanceAccount ? '/ No financial account linked to this user' : ''}`
                  : null}
                options={creditsAutocompleteOptions}
                disabled={readonly}
                className={clsx(
                  cls.selectField,
                  cls.autocompleteField,
                )}
                popupClassName={cls.popup}
                notFoundContent="Code is not found"
                allowClear
                onSelect={onCreditSelect}
                onSearch={onCreditSearch}
                onChange={onCreditChange}
                onClear={onCreditClear}
                placeholder="Search User code"
              />
            </>
          </Form.Item>

          <Form.Item
            label="Amount*"
            name="amount"
            className={clsx(cls.formItem, readonly && cls.disabled)}
            labelCol={labelCol}
            rules={[
              { required: true, message: '' },
              { type: 'number', message: 'Only numbers are allowed' },
              () => ({
                validator(_, value) {
                  const { creditId } = form.getFieldsValue(['creditId']);
                  const credit = options.credits.find(c => c.id === creditId);
                  if (value === 0 || value === undefined) {
                    return Promise.reject(new Error(''));
                  }
                  if (!credit || !credit.balance) {
                    if (value > 999999999 && !isAirtable) {
                      return Promise.reject(new Error('Max amount is $999,999,999'));
                    }
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber
              disabled={readonly}
              formatter={(val) => `$ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(val) => val ? +val.replace(/\$\s?|(,*)/g, '') : 0.01}
              bordered={false}
              className={clsx(cls.inputField, cls.moneyField)}
              controls={false}
              onBeforeInputCapture={onBeforeNumberInput}
            />
          </Form.Item>

          <Form.Item label={false} name="isUsd" hidden />

          {isUsd ? (
            <Form.Item
              label="Exch. Rate*"
              name="exchangeRate"
              className={clsx(cls.formItem, readonly && cls.disabled)}
              labelCol={labelCol}
              rules={[
                { required: true, message: '' },
                { type: 'number', message: 'Only numbers are allowed' },
                () => ({
                  validator(_, value) {
                    const { creditId } = form.getFieldsValue(['creditId']);
                    const credit = options.credits.find(c => c.id === creditId);
                    if (value === 0 || value === undefined) {
                      return Promise.reject(new Error(''));
                    }
                    if (!credit || !credit.balance) {
                      if (value > 999999999 && !isAirtable) {
                        return Promise.reject(new Error('Max amount is $999,999,999'));
                      }
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber
                disabled={readonly}
                formatter={(val) => `$ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(val) => val ? +val.replace(/\$\s?|(,*)/g, '') : 0.01}
                bordered={false}
                className={clsx(cls.inputField, cls.moneyField)}
                controls={false}
                onBeforeInputCapture={onBeforeNumberInput}
              />
            </Form.Item>
          ) : null}

          {isUsd ? (
            <Form.Item
              label="Amount USD"
              name="amountUsd"
              className={clsx(cls.formItem, readonly && cls.disabled)}
              labelCol={labelCol}
              rules={[
                { required: true, message: '' },
                { type: 'number', message: 'Only numbers are allowed' },
                () => ({
                  validator(_, value) {
                    const { creditId } = form.getFieldsValue(['creditId']);
                    const credit = options.credits.find(c => c.id === creditId);
                    if (value === 0 || value === undefined) {
                      return Promise.reject(new Error(''));
                    }
                    if (!credit || !credit.balance) {
                      if (value > 999999999 && !isAirtable) {
                        return Promise.reject(new Error('Max amount is $999,999,999'));
                      }
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber
                disabled={true}
                formatter={(val) => `$ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' USD'}
                parser={(val) => val ? +val.replace(/\$\s?|(,*)/g, '') : 0.01}
                bordered={false}
                className={clsx(cls.inputField, cls.moneyField)}
                controls={false}
                onBeforeInputCapture={e => e.preventDefault()}
              />
            </Form.Item>
          ) : null}

          <div className={cls.customInputRow}>
            <div className={clsx(
              cls.customInputLabelCol,
              readonly && cls.disabled,
            )}>Payment date*:
            </div>
            <div className={cls.customInputValueCol}>
              <DatePicker
                disabled={readonly}
                format="DD-MM-YYYY"
                placeholder="DD-MM-YYYY"
                onChange={onDateChange}
                defaultValue={moment(date, 'DD-MM-YYYY')}
                className={cls.dateInput}
                bordered={false}
                allowClear={false}
              />
            </div>
          </div>
          <div className={cls.customInputRow}>
            <div className={clsx(cls.customInputLabelCol, cls.disabled)}>Account #:</div>
            <div className={cls.customInputValueCol}>
              <Input bordered={false}
                className={cls.inputField}
                disabled
                value={account?.account || ''} />
            </div>
          </div>
          <div className={cls.customInputRow}>
            <div className={clsx(cls.customInputLabelCol, cls.disabled)}>Accredited:</div>
            <div className={cls.customInputValueCol}>
              <Input bordered={false} className={cls.inputField} disabled value={clientName} />
            </div>
          </div>
          <Form.Item
            label="Purpose*"
            name="purpose"
            style={{ marginTop: 22 }}
            className={clsx(cls.formItem, cls.purposeField, readonly && cls.disabled)}
            labelCol={labelCol}
            rules={[{ required: true, message: '' }]}
          >
            <Select name="purpose"
              className={cls.selectField}
              disabled={readonly}
              variant="outlined">
              {options.purpose.map(value =>
                <MenuItem key={value} value={value}>{value}</MenuItem>,
              )}
            </Select>
          </Form.Item>

          {showOrders && (
            <Form.Item
              label="Order"
              name="orderIds"
              className={clsx(
                cls.formItem,
                cls.purposeField,
                (!hasOrders || readonly) && cls.disabled,
              )}
              labelCol={labelCol}
              initialValue={selectedOrderIds}
            >
              {prefilledOrder ? (
                <Link to={`/order/${prefilledOrder.id}`} target="_blank">
                  {prefilledOrder.code}
                </Link>
              ) : (
                <Select
                  multiple={true}
                  name="purpose"
                  className={cls.selectField}
                  disabled={!hasOrders || readonly}
                  variant="outlined"
                  placeholder={!hasOrders ? 'No orders' : ''}
                  renderValue={(selectedIds: number[]) => (
                    <Box sx={{ display: 'flex', gap: 0.5 }} className={cls.orders}>
                      {hasOrders ? selectedIds.map((id) => (
                        <span key={id} className={cls.order}>
                          {ordersListMap[id]?.code || ''} - {formatMoney(ordersListMap[id]?.totalCost || 0)}
                        </span>
                      )) : 'No orders'}
                    </Box>
                  )}
                  onChange={onOrdersChange}
                >
                  {hasOrders && ordersList.map(value =>
                    <MenuItem key={value.id} value={value.id}>
                      {value.code} - {formatMoney(value.totalCost)}
                    </MenuItem>,
                  )}
                </Select>
              )}
            </Form.Item>
          )}
          <Form.Item
            label="Details"
            name="details"
            className={clsx(cls.formItem, readonly && cls.disabled)}
            labelCol={labelCol}
            rules={isAirtable ? [] : [
              { max: 100, message: 'Details should be less then 100 characters' },
            ]}
          >
            <Input.TextArea
              bordered={false}
              className={clsx(cls.inputField, cls.inputTextarea)}
              disabled={readonly}
            />
          </Form.Item>

          {/* Sent/Received checkboxes*/}
          <Row>
            <Col span={5}></Col>
            <Col span={6}>
              <Form.Item
                name="sent"
                valuePropName="checked"
                className={clsx(cls.formItem, cls.checkboxItem)}
                labelCol={{ span: 0 }}
                wrapperCol={{ span: 24 }}
              >
                <Checkbox
                  className={clsx(cls.checkboxField, !hasId && cls.disabled)}
                  disabled={!hasId}
                >Sent</Checkbox>
              </Form.Item>
            </Col>
            <Col span={1}></Col>
            <Col span={1}></Col>
            <Col span={6}>
              <Form.Item
                name="received"
                valuePropName="checked"
                className={clsx(cls.formItem, cls.checkboxItem)}
                labelCol={{ span: 0 }}
                wrapperCol={{ span: 24 }}
              >
                <Checkbox
                  className={clsx(cls.checkboxField, !hasId && cls.disabled)}
                  disabled={!hasId}
                >Received</Checkbox>
              </Form.Item>
            </Col>
            <Col span={5}></Col>
          </Row>

          {/* Fee/Percentage checkbox input */}
          <Row style={{ display: showFee ? undefined : 'none' }}>
            <Col span={6}></Col>
            <Col span={6}>
              <Form.Item
                name="fee"
                valuePropName="checked"
                className={clsx(cls.formItem, cls.checkboxItem)}
                labelCol={{ span: 0 }}
                wrapperCol={{ span: 24 }}
              >
                <Checkbox className={clsx(cls.checkboxField, cls.checkboxFieldFee)}>
                  Add Fee
                </Checkbox>
              </Form.Item>
            </Col>
            <Col span={4} style={{ display: isFee ? undefined : 'none' }}>
              <Form.Item
                label=""
                name="feePercent"
                className={clsx(cls.formItem, cls.formPercent, readonly && cls.disabled)}
                labelCol={labelCol}
              >
                <InputNumber
                  disabled={readonly || !isFee}
                  bordered={false}
                  className={clsx(cls.inputField, cls.moneyField)}
                  controls={false}
                  maxLength={5}
                  value={feePercent || null}
                  onChange={value => form.setFieldValue('feePercent', value)}
                />
                <span className={cls.formPercentText}>%</span>
              </Form.Item>
            </Col>
            <Col span={8}></Col>
          </Row>
        </Col>

        {/* Receipts & Invoices */}
        <Col xs={24} xl={12} className={cls.infoCol}>
          <div className={cls.fileFieldWrap}>
            <div className={cls.fileFieldRow}>
              <div className={cls.fileFieldLabel}>Receipts</div>
            </div>
            <div className={cls.fileField}>
              <FinanceDocuments
                isWithdrawal
                code={financeWithdrawal?.code}
                documents={financeWithdrawal ? receipts : undefined}
                onUpload={onUpload('receipt')}
                onRemove={onRemove('receipt')}
                canSave={!!financeWithdrawal}
              />
            </div>
          </div>
          <div className={cls.fileFieldWrap}>
            <div className={cls.fileFieldRow}>
              <div className={cls.fileFieldLabel}>Invoices</div>
              <div className={cls.uuid}>
                <span>UUID</span>
                <Form.Item
                  name="uuid"
                >
                  <Input type="text" onChange={onUuidChangeHandler} />
                </Form.Item>
              </div>
            </div>
            <div className={cls.fileField}>
              <FinanceDocuments
                isWithdrawal
                code={financeWithdrawal?.code}
                documents={currentInvoice
                  ? [
                    ...(invoices.filter(i => i.id !== currentInvoice.file.id)),
                    currentInvoice.file,
                  ] : invoices}
                onUpload={onUpload('invoice')}
                onRemove={onRemove('invoice')}
                canSave={!!financeWithdrawal}
              />
            </div>
          </div>
          <div className={cls.invoices}>
            {currentInvoice && (
              <Link to={`/credit/${currentInvoice.creditId}?invoices=${currentInvoice.id}`}>
                Invoice {currentInvoice.invoiceNumber}
              </Link>
            )}
          </div>
        </Col>
      </Row>

      <div className={cls.actions}>
        <Button
          variant="contained"
          color="secondary"
          className={cls.save}
          disabled={loading}
          onClick={submit}
        >
          {loading ? <LoadingOutlined /> : <SaveIcon />} Save
        </Button>

        {readonly && onDelete ? (
          <Button variant="contained" className={cls.delete} onClick={onDelete}>Delete</Button>
        ) : null}
      </div>
    </div>
  );
};
